import { useState } from 'react';
import { Clock, Compass, Heart, ChevronRight, Check, Award } from 'lucide-react';
import type { Expedition } from '../types';

interface ExpeditionCardProps {
	key?: string;
	expedition: Expedition;
	isSaved: boolean;
	onToggleSave: () => void;
	onViewItinerary: () => void;
	onBookNow: () => void;
}

export default function ExpeditionCard({
	expedition,
	isSaved,
	onToggleSave,
	onViewItinerary,
	onBookNow,
}: ExpeditionCardProps) {
	const [imageLoaded, setImageLoaded] = useState(false);

	return (
		<div
			id={`expedition-card-${expedition.id}`}
			className='bg-white rounded-xl shadow-md border border-earth-sand/30 overflow-hidden hover:shadow-2xl hover:scale-[1.01] transition-all duration-300 ease-in-out flex flex-col h-full group'
		>
			{/* Dynamic Cover Media */}
			<div className='relative h-64 sm:h-72 overflow-hidden bg-earth-sand/20'>
				{/* Shimmer skeleton loader */}
				{!imageLoaded && (
					<div className='absolute inset-0 bg-linear-to-r from-earth-sand/10 via-earth-sand/30 to-earth-sand/10 animate-pulse flex flex-col items-center justify-center space-y-2'>
						<Compass className='w-8 h-8 text-brand-gold/40 animate-spin [animation-duration:12s]' />
						<span className='font-sans text-[9px] tracking-widest text-brand-gold/50 uppercase font-bold'>
							Patron Portal Load
						</span>
					</div>
				)}

				<img
					src={expedition.image}
					alt={expedition.title}
					loading='lazy'
					decoding='async'
					onLoad={() => setImageLoaded(true)}
					className={`w-full h-full object-cover object-center group-hover:scale-105 transition-all duration-700 ease-in-out ${
						imageLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
					}`}
					referrerPolicy='no-referrer'
				/>

				{/* Destination Badges / Best Seller Badge */}
				{expedition.badge && (
					<div className='absolute top-4 left-4 z-10'>
						<span className='bg-brand-gold text-brand-forest-dark px-2.5 py-1 rounded-md font-sans text-[10px] tracking-wider uppercase font-extrabold shadow-md flex items-center gap-1.5'>
							<Award className='w-3.5 h-3.5 text-brand-forest-dark' />
							{expedition.badge}
						</span>
					</div>
				)}

				{/* Favorite Bookmark Bubble */}
				<button
					onClick={e => {
						e.stopPropagation();
						onToggleSave();
					}}
					className='absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/90 backdrop-blur-xs shadow-md hover:bg-white text-brand-charcoal hover:text-brand-terracotta active:scale-90 transition-all duration-300 ease-in-out flex items-center justify-center cursor-pointer'
					aria-label='Save this expedition'
				>
					<Heart
						className={`w-4 h-4 transition-colors duration-300 ease-in-out ${
							isSaved ? 'fill-brand-terracotta text-brand-terracotta' : 'text-brand-charcoal-light'
						}`}
					/>
				</button>

				<div className='absolute inset-0 bg-linear-to-t from-black/60 via-transparent to-transparent'></div>

				{/* Floating duration indicator */}
				<div className='absolute bottom-4 left-4 flex items-center space-x-1.5 text-white font-sans text-xs font-medium'>
					<Clock className='w-4 h-4 text-brand-gold' />
					<span>
						{expedition.durationText || `${expedition.durationDays} Days / ${expedition.durationDays - 1} Nights`}
					</span>
				</div>
			</div>

			{/* Main Body */}
			<div className='p-6 flex-1 flex flex-col justify-between'>
				{/* Location Header */}
				<div>
					<div className='flex justify-between items-center mb-3'>
						<span className='font-sans text-[11px] tracking-widest text-brand-gold-dark font-semibold uppercase'>
							{expedition.location} • {expedition.country}
						</span>
					</div>

					{/* Title */}
					<h3 className='font-serif text-xl sm:text-2xl text-brand-forest font-semibold leading-snug mb-2 group-hover:text-brand-forest-light transition-colors duration-300 ease-in-out'>
						{expedition.title}
					</h3>

					<p className='font-sans text-xs text-brand-charcoal-light italic mb-4 font-light'>{expedition.subtitle}</p>

					{/* Decadent Short Description */}
					<p className='font-sans text-xs leading-relaxed text-brand-charcoal-light/90 mb-5 font-light line-clamp-3'>
						{expedition.description}
					</p>

					{/* Highlights Mini list */}
					<div className='space-y-2.5 mb-6 border-t border-b border-earth-sand/30 py-4'>
						<h4 className='font-sans text-[10px] font-bold tracking-widest text-brand-forest uppercase'>
							Expedition Highlights
						</h4>
						<div className='space-y-1.5'>
							{expedition.highlights.slice(0, 3).map((high, index) => (
								<div key={index} className='flex items-start space-x-2 text-xs'>
									<Check className='w-3.5 h-3.5 text-brand-gold shrink-0 mt-0.5' />
									<span className='font-sans text-brand-charcoal-light font-light leading-tight'>{high}</span>
								</div>
							))}
						</div>
					</div>
				</div>

				{/* Footer & Action Panel */}
				<div className='pt-2'>
					{/* Interactive Action Hub */}
					<div className='grid grid-cols-2 gap-3'>
						<button
							onClick={onViewItinerary}
							className='w-full flex items-center justify-center space-x-1.5 border border-earth-sand hover:border-brand-forest hover:bg-earth-sand/20 active:scale-95 text-brand-forest font-sans text-xs uppercase tracking-widest font-semibold py-3 rounded-lg transition-all duration-300 ease-in-out cursor-pointer'
						>
							<span>Itinerary</span>
							<ChevronRight className='w-3 h-3' />
						</button>
						<button
							onClick={onBookNow}
							className='w-full bg-brand-forest hover:bg-brand-forest-light active:scale-95 text-earth-cream font-sans text-xs uppercase tracking-widest font-semibold py-3 rounded-lg transition-all duration-300 ease-in-out cursor-pointer shadow-sm text-center'
						>
							Inquire
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}
